'use client';

import React, { useMemo, useEffect, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { Box, Paper, Typography, CircularProgress } from '@mui/material';
import { ModuleRegistry, ValidationModule } from 'ag-grid-community';
import {
  ClientSideRowModelModule,
  ClipboardModule,
  ExcelExportModule,
  ColumnMenuModule,
  ContextMenuModule,
  CellSelectionModule,
  TextFilterModule,
  NumberFilterModule,
  DateFilterModule,
  RowStyleModule,
  RowSelectionModule,
  SetFilterModule,
  MultiFilterModule,
  GroupFilterModule,
  RowGroupingModule,
  RowGroupingPanelModule,
  FiltersToolPanelModule,
} from 'ag-grid-enterprise';

ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  ClipboardModule,
  ExcelExportModule,
  ColumnMenuModule,
  ContextMenuModule,
  CellSelectionModule,
  TextFilterModule,
  NumberFilterModule,
  DateFilterModule,
  RowStyleModule,
  RowSelectionModule,
  SetFilterModule,
  MultiFilterModule,
  GroupFilterModule,
  RowGroupingModule,
  RowGroupingPanelModule,
  FiltersToolPanelModule,

  ValidationModule /* Development Only */,
]);

import ProgressBarCellRenderer from '../ProjectTracker/ProgressBarCellRenderer';

const POVendorGrid = ({ purchaseOrders, loading, error, onLoaded }) => {
  const hasNotifiedLoaded = useRef(false);

  useEffect(() => {
    if (!loading && purchaseOrders?.length > 0 && !hasNotifiedLoaded.current) {
      hasNotifiedLoaded.current = true;
      onLoaded?.();
    }
  }, [loading, purchaseOrders, onLoaded]);

  const defaultColDef = useMemo(() => {
    return {
      flex: 1,
      minWidth: 300,
      filter: true,
    };
  }, []);

  const columnDefs = [
    {
      headerName: 'Subsidiary',
      field: 'subsidiaryname',
      hide: true,
      enableRowGroup: true,
      rowGroup: true,
    },
    {
      headerName: 'Net Amount',
      field: 'netamount',
      valueFormatter: (params) =>
        new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(params.value),
      aggFunc: 'sum',
      enableValue: true,
    },
    {
      headerName: 'Quantity Billed',
      field: 'quantitybilled',
      valueFormatter: (params) =>
        new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(params.value),
      aggFunc: 'sum',
      enableValue: true,
    },
    {
      headerName: 'PO Over/Under',
      field: 'valOverUnder',
      valueGetter: function (params) {
        if (!params.data || params.data.quantitybilled === undefined || params.data.netamount === undefined) {
          return 0;
        }
        const quantitybilled = params.data.quantitybilled ?? 0;
        const netamount = params.data.netamount ?? 0;

        if (netamount !== 0) {
          return quantitybilled - netamount;
        } else {
          return 0;
        }
      },
      valueFormatter: (params) =>
        new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(params.value),
      cellStyle: function (params) {
        if (params.value < 0) {
          return { color: '#00BBA1', fontWeight: 'bold' };
        } else if (params.value > 0) {
          return { color: '#FF594B', fontWeight: 'bold' };
        } else {
          return null;
        }
      },
      enableRowGroup: true,
      aggFunc: 'sum',
      enableValue: true,
    },
    {
      headerName: '% Billed',
      field: 'pctBilled',
      cellRenderer: ProgressBarCellRenderer,
      valueGetter: function (params) {
        if (!params.node.group && params.data) {
          const quantitybilled = params.data.quantitybilled ?? 0;
          const netamount = params.data.netamount ?? 0;

          return netamount !== 0 ? (quantitybilled / netamount) * 100 : 0;
        }
        return 0;
      },
      aggFunc: function (nodes) {
        let sumTotalBilled = 0;
        let sumNetAmount = 0;

        if (Array.isArray(nodes.rowNode.allLeafChildren)) {
          nodes.rowNode.allLeafChildren.forEach(function (node) {
            sumTotalBilled += node.data?.quantitybilled ?? 0;
            sumNetAmount += node.data?.netamount ?? 0;
          });
        }

        return sumNetAmount !== 0 ? (sumTotalBilled / sumNetAmount) * 100 : 0;
      },
      enableValue: true,
    },
    {
      headerName: 'Total Unbilled',
      field: 'unbilled',
      valueFormatter: (params) =>
        new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(params.value),
      cellStyle: function (params) {
        if (params.value > 0) {
          return { color: '#00BBA1', fontWeight: 'bold' };
        } else if (params.value < 0) {
          return { color: '#FF594B', fontWeight: 'bold' };
        } else {
          return null;
        }
      },
      aggFunc: 'sum',
      enableValue: true,
    },
    {
      headerName: 'Total Accrued',
      field: 'totalaccruedamount',
      valueFormatter: (params) =>
        new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(params.value),
      aggFunc: 'sum',
      enableValue: true,
    },
    {
      headerName: 'Tax Amount',
      field: 'taxamount',
      valueFormatter: (params) =>
        new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(params.value),
      aggFunc: 'sum',
      enableValue: true,
    },
    {
      headerName: 'AFE Number',
      field: 'afenumber',
    },
    {
      headerName: 'AFE Name',
      field: 'afename',
    },
    {
      headerName: 'Purchase Order Date',
      field: 'trandate',
    },
    {
      headerName: 'Required By Date',
      field: 'requiredbydate',
    },
    {
      headerName: 'Vendor',
      field: 'vendorname',
    },
    {
      headerName: 'Purchase Order Status',
      field: 'transactionstatus',
      rowGroup: true,
      enableRowGroup: true,
    },

    {
      headerName: 'Purchase Order',
      field: 'trandisplayname',
      rowGroup: true,
      enableRowGroup: true,
    },
    {
      headerName: 'Acct Number',
      field: 'acctnumber',
    },
    {
      headerName: 'Acct Type',
      field: 'accttype',
    },
    {
      headerName: 'Acct Name',
      field: 'accountsearchdisplayname',
    },
    {
      headerName: 'Memo',
      field: 'memo',
    },

    {
      headerName: 'AFE is Complete',
      field: 'iscomplete',
      sortable: true,
      filter: true,
      enableRowGroup: true,
      pivot: true,
      hide: true,
    },
  ];

  return (
    <Box>
      {loading ? (
        <Box sx={{ textAlign: 'center', padding: 3 }}>
          <CircularProgress />
          <Typography variant="h6">Loading Data...</Typography>
        </Box>
      ) : error ? (
        <Box sx={{ textAlign: 'center', color: 'red', padding: 3 }}>
          <Typography variant="h6">Error: {error}</Typography>
        </Box>
      ) : (
        <AgGridReact
          columnDefs={columnDefs}
          rowData={purchaseOrders}
          //pagination={true}
          defaultColDef={defaultColDef}
          rowGroupPanelShow={'always'}
          sideBar={'filters'}
          domLayout="autoHeight"
        />
      )}
    </Box>
  );
};

export default POVendorGrid;
